import React, { useState, useEffect } from "react";
import {
  getLeadNotes,
  addLeadNotes,
  deleteLeadNote,
  updateLeadNote,
} from "../../../services/lead.services";
import { useSelector } from "react-redux";
import { formatDistanceToNow } from "date-fns";
import toast from "react-hot-toast";
import {
  FaSpinner,
  FaTrash,
  FaEdit,
  FaCheck,
  FaSave,
  FaTimes,
  FaPlus,
  FaRegStickyNote,
} from "react-icons/fa";

function NotesTab({ fetchLeads }) {
  const selectedLead = useSelector((state) => state.leads.selectedLead);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedNoteText, setEditedNoteText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDropMessageChecked, setIsDropMessageChecked] = useState(false);
  const userDetails = useSelector((state) => state.user.userDetails);

  useEffect(() => {
    if (selectedLead) {
      fetchNotes();
    }
  }, [selectedLead]);

  const fetchNotes = async () => {
    setIsLoading(true);
    try {
      const res = await getLeadNotes(selectedLead);
      setNotes(res.data);
    } catch (err) {
      console.error("Failed to fetch notes:", err);
      toast.error("Unable to load notes");
      setNotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note before submitting.");
      return;
    }

    setIsLoading(true);
    try {
      const noteData = { lead: selectedLead.id, note: newNote };
      await addLeadNotes(selectedLead, noteData);
      setNewNote("");
      toast.success("Note added successfully!");
      await fetchNotes(); // Refresh notes after adding
      fetchLeads(); // Update lead data
    } catch (err) {
      console.error("Failed to add note:", err);
      toast.error("Error adding note.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditedNoteText(note.note);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditedNoteText("");
  };

  const handleUpdateNote = async (e, note) => {
    e.preventDefault();

    if (!editedNoteText.trim()) {
      toast.error("Note cannot be empty.");
      return;
    }

    if (note.user_name !== userDetails.username) {
      toast.error("You can only edit your own notes.");
      return;
    }

    setIsLoading(true);
    try {
      const updatedData = { note: editedNoteText };
      await updateLeadNote(selectedLead.id, editingNoteId, updatedData);
      await fetchNotes(); // Refresh notes after updating
      setEditingNoteId(null);
      setEditedNoteText("");
      toast.success("Note updated successfully!");
    } catch (err) {
      console.error("Failed to update note:", err);
      toast.error("Error updating note.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNote = async (noteId, note) => {
    if (note.user_name !== userDetails.username) {
      toast.error("You can only delete your own notes.");
      return;
    }

    if (window.confirm("Are you sure you want to delete this note?")) {
      setIsLoading(true);
      try {
        await deleteLeadNote(selectedLead.id, noteId);
        await fetchNotes(); // Refresh notes after deleting
        toast.success("Note deleted successfully!");
      } catch (err) {
        console.error("Failed to delete note:", err);
        toast.error("Error deleting note.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDropMessageToggle = () => {
    const newCheckedState = !isDropMessageChecked;
    setIsDropMessageChecked(newCheckedState);
    if (newCheckedState) {
      setNewNote("Call not received, drop a message.");
    } else {
      setNewNote("");
    }
  };

  return (
    <div className="notes-tab h-full flex flex-col bg-white p-4 rounded-lg overflow-hidden">
      <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center">
        <FaRegStickyNote className="mr-2 text-indigo-600" />
        Notes for {selectedLead?.name || "Lead"}
      </h2>

      {/* Add New Note */}
      <div className="mb-6">
        <div className="mb-2">
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 transition-colors focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            placeholder="Write a note..."
            rows="3"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <button
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAddNote}
            disabled={isLoading}
          >
            {isLoading ? (
              <FaSpinner className="animate-spin mr-2" />
            ) : (
              <FaPlus className="mr-2" />
            )}
            Add Note
          </button>

          <div
            className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer max-w-max"
            onClick={handleDropMessageToggle}
          >
            <div className="relative">
              <input
                type="checkbox"
                id="drop-message"
                className="peer sr-only"
                checked={isDropMessageChecked}
                onChange={handleDropMessageToggle}
              />
              <div className="w-5 h-5 border-2 border-gray-300 rounded flex items-center justify-center peer-checked:border-indigo-500 peer-checked:bg-indigo-500 transition-all">
                {isDropMessageChecked && (
                  <FaCheck className="text-white text-xs" />
                )}
              </div>
            </div>
            <label
              htmlFor="drop-message"
              className="text-sm font-medium text-gray-700 cursor-pointer"
            >
              Call not received, Drop a Message
            </label>
          </div>
        </div>
      </div>

      {/* Notes List */}
      <div className="flex-grow overflow-hidden">
        <h3 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
          Note History
        </h3>

        {isLoading && notes.length === 0 ? (
          <div className="flex justify-center items-center py-8">
            <FaSpinner className="animate-spin text-2xl text-indigo-600" />
          </div>
        ) : (
          <div
            className="space-y-3 overflow-auto pb-20 h-full"
            // style={{ maxHeight: "calc(100vh - 62vh)" }}
          >
            {notes.length > 0 ? (
              notes.map((note, index) => (
                <div
                  key={note.id || index}
                  className={`rounded-lg p-4 transition-all ${
                    note.user_name === userDetails.username
                      ? "bg-indigo-50 border-l-4 border-indigo-500"
                      : "bg-gray-50 border-l-4 border-gray-300"
                  }`}
                >
                  {editingNoteId === note.id ? (
                    <div className="space-y-3">
                      <textarea
                        className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                        value={editedNoteText}
                        onChange={(e) => setEditedNoteText(e.target.value)}
                        rows="3"
                      />
                      <div className="flex justify-end space-x-2">
                        <button
                          className="bg-gray-200 text-gray-700 px-3 py-1 rounded-md hover:bg-gray-300 transition-colors flex items-center"
                          onClick={handleCancelEdit}
                        >
                          <FaTimes className="mr-1" /> Cancel
                        </button>
                        <button
                          className="bg-indigo-600 text-white px-3 py-1 rounded-md hover:bg-indigo-700 transition-colors flex items-center"
                          onClick={(e) => handleUpdateNote(e, note)}
                        >
                          <FaSave className="mr-1" /> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-800 whitespace-pre-line">
                        {note.note}
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-xs text-gray-500">
                          <span className="font-medium">{note.user_name}</span>{" "}
                          ·
                          {note.created_at && (
                            <span className="ml-1">
                              {formatDistanceToNow(new Date(note.created_at), {
                                addSuffix: true,
                              })}
                            </span>
                          )}
                        </p>

                        {note.user ===
                          (userDetails.admin_id ||
                            userDetails.superadmin_id) && (
                          <div className="flex space-x-2">
                            <button
                              className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-100 transition-colors"
                              onClick={() => handleEditNote(note)}
                              title="Edit Note"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-100 transition-colors"
                              onClick={() => handleDeleteNote(note.id, note)}
                              title="Delete Note"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-10 bg-gray-50 rounded-lg">
                <FaRegStickyNote className="mx-auto text-4xl text-gray-300 mb-3" />
                <p className="text-gray-500">No notes available.</p>
                <p className="text-sm text-gray-400 mt-2">
                  Add a note to keep track of important information.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default NotesTab;
